import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GripVertical, Plus, Trash2, Loader2 } from "lucide-react";

interface SuggestedConnectionRow {
  id: string;
  user_id: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  role: string | null;
}

export default function AdminSuggestedConnections() {
  const [connections, setConnections] = useState<SuggestedConnectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const fetchConnections = useCallback(async () => {
    try {
      // Fetch suggested connections
      const { data: suggestedData, error: suggestedError } = await supabase
        .from("suggested_connections")
        .select("id, user_id, display_order, is_active, created_at")
        .order("display_order", { ascending: true });

      if (suggestedError) throw suggestedError;

      if (!suggestedData || suggestedData.length === 0) {
        setConnections([]);
        setLoading(false);
        return;
      }

      // Get user IDs to fetch profiles
      const userIds = suggestedData.map((s) => s.user_id);

      // Fetch profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Fetch emails from profile_private
      const { data: privateData, error: privateError } = await supabase
        .from("profile_private")
        .select("user_id, email")
        .in("user_id", userIds);

      if (privateError) throw privateError;

      // Create lookup maps
      const profileMap = new Map(profilesData?.map((p) => [p.id, p]) || []);
      const emailMap = new Map(privateData?.map((p) => [p.user_id, p.email]) || []);

      // Combine data
      const combinedData: SuggestedConnectionRow[] = suggestedData.map((s) => {
        const profile = profileMap.get(s.user_id);
        return {
          id: s.id,
          user_id: s.user_id,
          display_order: s.display_order,
          is_active: s.is_active,
          created_at: s.created_at,
          full_name: profile?.full_name || null,
          email: emailMap.get(s.user_id) || null,
          avatar_url: profile?.avatar_url || null,
          role: profile?.role || null,
        };
      });

      setConnections(combinedData);
    } catch (err) {
      console.error("Error fetching suggested connections:", err);
      toast.error("Failed to load suggested connections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleAddUser = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setAdding(true);
    try {
      // Look up user by email in profile_private
      const { data: privateProfile, error: privateError } = await supabase
        .from("profile_private")
        .select("user_id, email")
        .eq("email", newEmail.trim().toLowerCase())
        .single();

      if (privateError || !privateProfile) {
        toast.error("User not found with that email address");
        return;
      }

      // Get profile name
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", privateProfile.user_id)
        .single();

      // Check if already in suggested connections
      const { data: existing } = await supabase
        .from("suggested_connections")
        .select("id")
        .eq("user_id", privateProfile.user_id)
        .single();

      if (existing) {
        toast.error("This user is already in the suggested connections list");
        return;
      }

      // Get max display order
      const maxOrder = connections.length > 0
        ? Math.max(...connections.map((c) => c.display_order))
        : 0;

      // Insert new suggested connection
      const { error: insertError } = await supabase
        .from("suggested_connections")
        .insert({
          user_id: privateProfile.user_id,
          display_order: maxOrder + 1,
          is_active: true,
        });

      if (insertError) throw insertError;

      toast.success(`Added ${profile?.full_name || privateProfile.email} to suggested connections`);
      setNewEmail("");
      fetchConnections();
    } catch (err) {
      console.error("Error adding user:", err);
      toast.error("Failed to add user to suggested connections");
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateDisplayOrder = async (id: string, newOrder: number) => {
    if (isNaN(newOrder) || newOrder < 1) return;

    setUpdatingIds((prev) => new Set(prev).add(id));
    try {
      const { error } = await supabase
        .from("suggested_connections")
        .update({ display_order: newOrder })
        .eq("id", id);

      if (error) throw error;

      setConnections((prev) =>
        prev
          .map((c) => (c.id === id ? { ...c, display_order: newOrder } : c))
          .sort((a, b) => a.display_order - b.display_order)
      );
      toast.success("Display order updated");
    } catch (err) {
      console.error("Error updating display order:", err);
      toast.error("Failed to update display order");
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    setUpdatingIds((prev) => new Set(prev).add(id));
    try {
      const { error } = await supabase
        .from("suggested_connections")
        .update({ is_active: !currentActive })
        .eq("id", id);

      if (error) throw error;

      setConnections((prev) =>
        prev.map((c) => (c.id === id ? { ...c, is_active: !currentActive } : c))
      );
      toast.success(`User ${!currentActive ? "activated" : "deactivated"}`);
    } catch (err) {
      console.error("Error toggling active status:", err);
      toast.error("Failed to update active status");
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from("suggested_connections")
        .delete()
        .eq("id", deleteId);

      if (error) throw error;

      setConnections((prev) => prev.filter((c) => c.id !== deleteId));
      toast.success("User removed from suggested connections");
    } catch (err) {
      console.error("Error deleting suggested connection:", err);
      toast.error("Failed to remove user");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const capitalizeRole = (role: string | null) => {
    if (!role) return "Unknown";
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Suggested Connections Management</CardTitle>
        <CardDescription>
          Configure which users appear as suggested connections for new members
          during onboarding. Admin-suggested users appear in the "Connect with
          the EABConnect Team" section.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add new user section */}
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter user email to add..."
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddUser()}
            className="flex-1"
          />
          <Button onClick={handleAddUser} disabled={adding}>
            {adding ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add User
          </Button>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty state */}
        {!loading && connections.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No suggested connections configured. Add users using the form above.
          </div>
        )}

        {/* Connections table */}
        {!loading && connections.length > 0 && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="w-24">Order</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id}>
                    <TableCell>
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage
                            src={connection.avatar_url || undefined}
                            alt={connection.full_name || "User"}
                          />
                          <AvatarFallback>
                            {getInitials(connection.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {connection.full_name || "Unknown User"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {connection.email || "No email"}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {capitalizeRole(connection.role)}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        value={connection.display_order}
                        onChange={(e) =>
                          handleUpdateDisplayOrder(
                            connection.id,
                            parseInt(e.target.value, 10)
                          )
                        }
                        className="w-16 h-8"
                        disabled={updatingIds.has(connection.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant={connection.is_active ? "default" : "outline"}
                        size="sm"
                        onClick={() =>
                          handleToggleActive(connection.id, connection.is_active)
                        }
                        disabled={updatingIds.has(connection.id)}
                      >
                        {updatingIds.has(connection.id) ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : connection.is_active ? (
                          "Active"
                        ) : (
                          "Inactive"
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(connection.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Suggested Connection</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove this user from the suggested
                connections list? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
